import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRootScope } from 'FrontRoyalAngular';
import { userActionsApi } from 'Users';
import { showToast } from 'Toast';

// Persists a subset of user fields via RTK updateCurrentUser (PUT /api/users.json, the endpoint the
// old Iguana userProxy.save() hit) and toasts the result.
//
// Deliberately omits `updated_at` and `meta.education_experiences`: both are only read in
// users_controller#perform_update's is_admin branch, so they were inert for learners.
//
// set_user_push_messages echoes the English-proficiency and transcript fields, so RTK syncs those
// itself; the address fields and phone aren't in that list, hence `syncToCurrentUser`.
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

export function useSaveDocuments() {
    const { t } = useTranslation('back_royal');
    const $rootScope = useRootScope();
    const [updateCurrentUser] = userActionsApi.useUpdateCurrentUserMutation();
    const [saving, setSaving] = useState(false);

    const save = useCallback(
        async ({
            fields,
            successKey,
            syncToCurrentUser = false,
        }: {
            fields: Record<string, unknown>;
            successKey: 'documents_saved' | 'mailing_address_saved';
            syncToCurrentUser?: boolean;
        }): Promise<boolean> => {
            const currentUser = $rootScope.currentUser;
            if (!currentUser) return false;

            setSaving(true);
            try {
                await updateCurrentUser({ user: { id: currentUser.id, ...fields } }).unwrap();

                if (syncToCurrentUser) {
                    const target = currentUser as unknown as Record<string, unknown>;
                    Object.entries(fields).forEach(([key, value]) => {
                        // Nested records are partial payloads (career_profile carries only id,
                        // place_id and place_details), so assigning would drop the rest of it.
                        if (isPlainObject(value) && isPlainObject(target[key])) {
                            Object.assign(target[key] as object, value);
                            return;
                        }
                        target[key] = value;
                    });
                    // Propagates the writes to Angular watchers. The DS toast is pure React, so it
                    // no longer flushes a digest as a side effect.
                    $rootScope.$applyAsync();
                }

                showToast({ message: t(`settings.edit_documents.${successKey}`), type: 'success' });
                return true;
            } catch {
                showToast({ message: t('settings.edit_documents.save_failed'), type: 'danger' });
                return false;
            } finally {
                setSaving(false);
            }
        },
        [$rootScope, updateCurrentUser, t],
    );

    return { save, saving };
}
